type FieldKeys<T> = keyof T & string;
type Dependencies<T> = { [K in FieldKeys<T>]?: () => unknown };
type LoaderDeps<T> = Partial<T> & { pattern?: string };
export type LoaderFn<T> = (deps: LoaderDeps<T>) => Promise<any[]> | any[];

/**
 * SelectLoader — источник опций для VSelect.
 *
 * ВАЖНО: поля приватны через TS (`private _field`), а НЕ через нативные `#field`.
 * Экземпляр живёт в data() формы и оборачивается Vue в reactive-прокси.
 * Нативные `#`-поля привязаны к конкретному экземпляру и НЕ читаются через
 * Proxy — обращение к ним из геттера, вызванного через прокси, падает с
 * "Cannot read private member from an object whose class did not declare it".
 * TS-private в рантайме — обычное свойство, прокси его проксирует нормально.
 *
 * ВАЖНО: источник рассчитан на один контрол. Он держит состояние загрузки
 * (isLoading), поэтому один экземпляр, отданный в два контрола, зажжёт
 * загрузку в обоих. Нужны два контрола — создавайте два источника.
 */
export default class SelectLoader<T = any> {

  /** Функция загрузки опций или null для статики. */
  private readonly _loader: LoaderFn<T> | null;

  /** Загруженные с бэкенда или статичные опции. */
  private _options: any[] = [];

  /** Опции, присутствующие в списке всегда — независимо от выдачи загрузчика. */
  private _pinOptions: any[] = [];

  /** Поле-идентификатор опции. По нему дедуплицируем список. */
  private _identity: string = 'id';

  /** Геттеры полей формы, от которых зависим. */
  private _dependencies: Dependencies<T> = {};

  /** Геттеры полей, которые уточняют выдачу, но без которых она осмысленна. */
  private _nullableDependencies: Dependencies<T> = {};

  /** Грузить опции сразу при создании контрола. */
  private _preloadOnMount: boolean = false;

  /** Грузить опции при первом открытии выпадашки. */
  private _preloadOnFocus: boolean = false;

  /** Перезагружать опции при смене любого родителя. */
  private _loadOnDependantChange: boolean = false;

  /** Слать запрос на бэкенд при вводе. */
  private _loadOnInputPattern: boolean = true;

  /** Загрузка фактически выполнялась (см. геттер isLoaded). */
  private _isLoaded: boolean = false;

  /** Идёт загрузка прямо сейчас. */
  private _isLoading: boolean = false;

  /** Источник контролу не передали — заглушка из from(null). */
  private _isEmptySource: boolean = false;

  /**
   * Селектор дефолтной опции. Получает загруженные опции и разрешённые
   * зависимости, возвращает опцию (или null). null — селектор не задан.
   */
  private _defaultSelector: ((options: any[], deps: Partial<T>) => unknown) | null = null;

  /**
   * Identity значения, подставленного автоматически. undefined — подстановки
   * не было либо значение с тех пор меняли вручную.
   *
   * Нужно, чтобы отличать «стоит дефолт» от «пользователь выбрал сам»: первое
   * можно пересчитать при смене родителя, второе трогать нельзя.
   */
  private _appliedIdentity: unknown = undefined;

  constructor(dataSource: LoaderFn<T> | any[]) {
    if (Array.isArray(dataSource)) {
      this._loader  = null;
      this._options = dataSource;
    } else {
      this._loader = dataSource;
    }
  }

  /**
   * Приводит проп data контрола к источнику: массив/функция → новый источник,
   * Map (в т.ч. Dictionary) → его значения, готовый экземпляр — как есть,
   * пустота → пустая заглушка. Заглушка нужна, чтобы контрол не проверял
   * источник на null в каждом геттере.
   *
   * Вызывается из data() контрола, чтобы источник был готов с первого рендера
   * и watcher dependencyKey не срабатывал ложно на маунте (значение ключа
   * стабильно с самого начала, а не прыгает null → ключ).
   */
  static from(data: unknown): SelectLoader {
    if (data === null || data === undefined) {
      const emptySource = new SelectLoader([]);
      emptySource._isEmptySource = true;
      return emptySource;
    } else if (data instanceof SelectLoader) {
      return data;
    } else if (data instanceof Map) {
      return new SelectLoader(Array.from(data.values()));
    } else {
      return new SelectLoader(data as LoaderFn<any> | any[]);
    }
  }

  // ==================== Fluent-конфигурация ====================

  /**
   * @example
   * .dependsOn({ sellerCompany: () => this.localOrder.sellerCompany })
   *
   * @example
   * .dependsOn({
   *   buyerCompany:  () => this.localOrder.buyerCompany,
   *   sellerCompany: () => this.localOrder.sellerCompany,
   * })
   */
  dependsOn(dependencies: Dependencies<T>): this {
    this._dependencies          = dependencies;
    this._loadOnDependantChange = true;
    this._loadOnInputPattern    = false;
    return this;
  }

  /**
   * Зависимости, которые уточняют выдачу, но пустыми её не отменяют.
   *
   * Отличие от dependsOn одно и важное: пустой родитель здесь не делает
   * источник незагружаемым. В dependsOn смысл обратный — «без этого грузить
   * нечего», и пустое значение честно гасит загрузку; поставь туда поле,
   * которое заполнено не всегда, и список исчезнет совсем.
   *
   * Пример: платёж начат с позиции заказа, и список отгрузок сужается до
   * тех, что её содержат. Позиции нет — показываем все отгрузки, а не
   * пустоту.
   *
   * На перезагрузку при смене влияет так же, как dependsOn: значение входит
   * в dependencyKey, контрол его сторожит. А вот поиск по вводу здесь не
   * отключается — необязательная зависимость серверному поиску не мешает.
   *
   * @example
   * .dependsOnNullable({ orderPosition: () => this.basisOrderPosition })
   */
  dependsOnNullable(dependencies: Dependencies<T>): this {
    this._nullableDependencies  = dependencies;
    this._loadOnDependantChange = true;
    return this;
  }

  /**
   * Опции, которые есть в списке всегда — независимо от того, что вернул загрузчик.
   * Пока загрузки не было, стоят в начале; после — встают за выдачей,
   * а совпавшие по identity подменяют собой загруженную версию.
   *
   * Принимает готовый массив или промис — промис разрешается в фоне,
   * опции появятся в списке по готовности (реактивность подхватит).
   *
   * @example
   * .pinOptions([Core.currentPartner])
   *
   * @example
   * .pinOptions(ProductCategoriesService.search({ isBookmarked: true }))
   */
  pinOptions(options: any[] | Promise<any[]>): this {
    if (options instanceof Promise) {
      options.then(resolved => {
        this._pinOptions = resolved;
      });
    } else {
      this._pinOptions = options;
    }

    return this;
  }

  /**
   * Поле-идентификатор опции. По нему источник дедуплицирует список,
   * а VSelectV2 строит ключи и сравнивает выбранное значение.
   *
   * @example
   * .setIdentity('code')
   */
  setIdentity(field: string): this {
    this._identity = field;
    return this;
  }

  /**
   * @example
   * .preloadOnMount()
   *
   * @example
   * .preloadOnMount(this.localOrder.isExisting === false)
   */
  preloadOnMount(condition: boolean = true): this {
    this._preloadOnMount     = condition;
    this._loadOnInputPattern = false;
    this.warnIfBothPreloads();
    return this;
  }

  /**
   * @example
   * .preloadOnFocus()
   *
   * @example
   * .preloadOnFocus(this.localOrder.isExisting)
   */
  preloadOnFocus(condition: boolean = true): this {
    this._preloadOnFocus     = condition;
    this._loadOnInputPattern = false;
    this.warnIfBothPreloads();
    return this;
  }

  /**
   * Селектор дефолтной опции. Получает загруженные опции и разрешённые
   * зависимости, возвращает опцию которую надо выбрать дефолтом.
   *
   * @example
   * .default((options, { sellerCompany, buyerCompany }) => {
   *   if (sellerCompany.id === 123 && buyerCompany.id === 456) {
   *     return options.find(option => option.id === 567);
   *   }
   * })
   */
  default(selector: (options: any[], deps: Partial<T>) => unknown): this {
    this._defaultSelector = selector;
    return this;
  }

  private warnIfBothPreloads(): void {
    if (this._preloadOnMount === true && this._preloadOnFocus === true) {
      console.warn('[SelectLoader] preloadOnMount и preloadOnFocus включены одновременно — это взаимоисключающие режимы. Оставлен preloadOnMount, preloadOnFocus сброшен.');
      this._preloadOnFocus = false;
    }
  }

  get hasDependency(): boolean {
    return Object.keys(this._dependencies).length > 0
        || Object.keys(this._nullableDependencies).length > 0;
  }

  get isPreloadOnMount(): boolean {
    return this._preloadOnMount;
  }

  get isPreloadOnFocus(): boolean {
    return this._preloadOnFocus;
  }

  get isLoadOnDependantChange(): boolean {
    return this._loadOnDependantChange;
  }

  get isLoadOnInputPattern(): boolean {
    return this._loadOnInputPattern;
  }

  /** Селектор дефолта задан через default(). */
  get hasDefault(): boolean {
    return this._defaultSelector !== null;
  }

  /** Поле-идентификатор опции — по нему VSelectV2 строит ключи и сравнивает выбранное. */
  getIdentity(): string {
    return this._identity;
  }

  /**
   * Опции для показа: загруженные, следом закреплённые.
   * Map даёт дедупликацию по identity и хранит порядок первой вставки — пока
   * загрузки не было, закреплённые стоят в начале; после встают за выдачей,
   * а совпавшие по identity подменяют загруженную версию, оставаясь на её месте.
   */
  get options(): any[] {
    const uniqueOptions = new Map();

    for (const option of this._options) {
      uniqueOptions.set(option[this._identity], option);
    }

    for (const option of this._pinOptions) {
      uniqueOptions.set(option[this._identity], option);
    }

    return [...uniqueOptions.values()];
  }

  /** Статичный источник: опции заданы массивом, загрузки нет. */
  get isStatic(): boolean {
    return this._loader === null;
  }

  /** Идёт загрузка прямо сейчас — контролы показывают индикатор. */
  get isLoading(): boolean {
    return this._isLoading;
  }

  /**
   * «Загрузили и пусто» — контрол по этому признаку показывает emptyDataText.
   * Пока данные не запрашивались (пустой родитель, прелоад не сработал) —
   * false: «ещё не грузили» это не «загрузили и пусто». Заглушка из from(null)
   * пустой выдачей тоже не считается — источника просто нет.
   */
  get isLoadedAndEmpty(): boolean {
    if (this._isEmptySource === true) {
      return false;
    } else if (this._isLoading === true) {
      return false;
    } else {
      return this.isLoaded === true && this.options.length === 0;
    }
  }

  /**
   * Данные фактически получены: загрузка выполнялась, либо источник статичный.
   * Ветка пустого родителя isLoaded не выставляет — данные не запрашивались.
   * Используется VSelect для empty-data-text: «загрузили и пусто» отличается
   * от «ещё не грузили».
   */
  get isLoaded(): boolean {
    if (this._loader === null) {
      return true;
    } else {
      return this._isLoaded;
    }
  }

  /**
   * Дефолтная опция от селектора default(). Получает загруженные опции и
   * разрешённые зависимости. null, если селектор не задан.
   */
  get defaultValue(): unknown {
    if (this._defaultSelector === null) {
      return null;
    } else {
      return this._defaultSelector(this.options, this.resolvedDependencies);
    }
  }

  /**
   * Контрол сообщает, какое значение он подставил автоматически.
   * Ручной выбор, снятие и очистка сбрасывают метку — для этого достаточно
   * передать пустое значение.
   *
   * Multiple не поддерживается: набор значений одним identity не описать,
   * метка просто сбрасывается и подстановка ведёт себя как раньше.
   */
  markApplied(value: unknown): void {
    if (value === null || value === undefined) {
      this._appliedIdentity = undefined;
    } else if (Array.isArray(value) === true) {
      this._appliedIdentity = undefined;
    } else {
      this._appliedIdentity = (value as Record<string, unknown>)[this._identity];
    }
  }

  /**
   * Переданное значение стоит с прошлой автоподстановки — его можно
   * пересчитать при смене родителя, не затирая осознанный выбор.
   */
  isApplied(value: unknown): boolean {
    if (this._appliedIdentity === undefined) {
      return false;
    } else if (value === null || value === undefined) {
      return false;
    } else if (Array.isArray(value) === true) {
      return false;
    } else {
      return (value as Record<string, unknown>)[this._identity] === this._appliedIdentity;
    }
  }

  /**
   * @example
   * // dependsOn({ seller: () => this.localOrder.seller })
   * source.resolvedDependencies // { seller: <текущий seller> }
   */
  get resolvedDependencies(): Partial<T> {
    return {
      ...this.resolve(this._dependencies),
      ...this.resolve(this._nullableDependencies),
    } as Partial<T>;
  }

  /** Разрешает набор геттеров в значения. */
  private resolve(dependencies: Dependencies<T>): Record<string, unknown> {
    const result: Record<string, unknown> = {};

    for (const key in dependencies) {
      const getter = dependencies[key];

      if (getter !== undefined) {
        result[key] = getter();
      }
    }

    return result;
  }

  /**
   * @example
   * // независимый источник (без dependsOn)
   * source.isDependentValueNotEmpty // true
   *
   * @example
   * // dependsOn({ seller }) при пустом seller
   * source.isDependentValueNotEmpty // false
   */
  get isDependentValueNotEmpty(): boolean {
    /** Только обязательные: необязательные на возможность загрузки не
     *  влияют, см. dependsOnNullable. */
    const values = Object.values(this.resolve(this._dependencies));

    if (values.length === 0) {
      return true;
    } else {
      return values.every(parent => parent !== null && parent !== undefined);
    }
  }

  /**
   * Ключ зависимостей — контрол вешает на него watch и перезагружает список
   * при смене родителя. Независимый источник — null, watch не сработает.
   *
   * @example
   * // dependsOn({ seller: () => seller#42, buyer: () => buyer#7 })
   * source.dependencyKey // "42|7"
   *
   * @example
   * // зависимость с пустым родителем
   * source.dependencyKey // "null|7"
   *
   * @example
   * // без dependsOn
   * source.dependencyKey // null
   */
  get dependencyKey(): string | null {
    if (this.hasDependency === false) {
      return null;
    } else {
      return this.buildDependencyKey();
    }
  }

  private buildDependencyKey(): string {
    return Object.values(this.resolvedDependencies)
        .map(parent => {
          if (parent === null || parent === undefined) {
            return 'null';
          } else if (typeof parent === 'object' && 'id' in parent) {
            return String((parent as { id: unknown }).id);
          } else {
            return String(parent);
          }
        })
        .join('|');
  }

  async load(pattern?: string): Promise<void> {
    if (this._loader === null) {
      return;
    } else if (this.isDependentValueNotEmpty === false) {
      /** Родитель опустел — данных больше нет и запрашивать их не у кого.
       *  isLoaded сбрасываем вместе с опциями: иначе после успешной загрузки
       *  и последующей очистки родителя источник выглядел бы как «загрузили и
       *  пусто», и контрол показал бы emptyDataText вместо приглашения
       *  выбрать родителя. */
      this._options  = [];
      this._isLoaded = false;
    } else {
      const deps = { ...this.resolvedDependencies, pattern } as LoaderDeps<T>;
      this._isLoading = true;
      try {
        const result = this._loader(deps);
        if (result instanceof Promise) {
          this._options = await result;
        } else {
          this._options = result;
        }
        this._isLoaded = true;
      } finally {
        // Ошибку не глушим — она уходит наверх, но флаг снимаем всегда,
        // иначе контрол залипает в состоянии загрузки навсегда.
        this._isLoading = false;
      }
    }
  }

  /** Предзагрузка при создании контрола, если источник так настроен. */
  async loadOnMount(): Promise<void> {
    if (this._preloadOnMount === false) {
      return;
    } else {
      await this.load();
    }
  }

  /**
   * Предзагрузка при первом открытии выпадашки, если источник так настроен.
   * Уже загруженные опции не перезапрашиваем — смена родителя обновляет их сама.
   *
   * true — загрузка фактически выполнялась, контролу есть что подставлять.
   */
  async loadOnFocus(): Promise<boolean> {
    if (this._preloadOnFocus === false) {
      return false;
    } else if (this.options.length > 0) {
      return false;
    } else {
      await this.load();
      return true;
    }
  }

  /**
   * Перезагрузка при смене родителя зависимого источника.
   * false — источник так не настроен и загрузки не было.
   */
  async reloadOnDependantChange(): Promise<boolean> {
    if (this._loadOnDependantChange === false) {
      return false;
    } else {
      await this.load();
      return true;
    }
  }
}